/**
 * Field type definitions, signer colors, and coordinate utilities
 * for the eSign visual PDF field editor and signing viewer.
 */

/**
 * Signer color palette. Each signer gets a unique color for their fields.
 * Colors are CSS-friendly and accessible against white PDF backgrounds.
 */
export const SIGNER_COLORS = [
  { bg: 'rgba(33, 150, 243, 0.25)', border: '#2196F3', text: '#1565C0', name: 'Blue' },
  { bg: 'rgba(76, 175, 80, 0.25)', border: '#4CAF50', text: '#2E7D32', name: 'Green' },
  { bg: 'rgba(255, 152, 0, 0.25)', border: '#FF9800', text: '#E65100', name: 'Orange' },
  { bg: 'rgba(0, 188, 212, 0.25)', border: '#00BCD4', text: '#006064', name: 'Cyan' },
  { bg: 'rgba(244, 67, 54, 0.25)', border: '#F44336', text: '#C62828', name: 'Red' },
  { bg: 'rgba(0, 150, 136, 0.25)', border: '#009688', text: '#00695C', name: 'Teal' },
  { bg: 'rgba(121, 85, 72, 0.25)', border: '#795548', text: '#4E342E', name: 'Brown' },
  { bg: 'rgba(96, 125, 139, 0.25)', border: '#607D8B', text: '#37474F', name: 'BlueGrey' },
];

/**
 * Get the color set for a signer by their index (0-based).
 */
export function getSignerColor(signerIndex) {
  return SIGNER_COLORS[signerIndex % SIGNER_COLORS.length];
}

/**
 * UI presentation metadata for each field type - icon, label, and tooltip
 * shown in the toolbar and properties panel. Sizing rules (default
 * dimensions, minimums) are NOT in this table - they live server-side in
 * AnnotationFieldTypeRules.cs and are fetched by the
 * useAnnotationFieldTypesStore Pinia store. Keeping presentation here and
 * sizing in the server table eliminates the drift hazard that existed when
 * both lived in JS and were tuned independently.
 */
export const FIELD_TYPES = {
  Signature:  { type: 'Signature',  label: 'Signature',     icon: 'draw',                tooltip: 'Signer draws or types their full signature' },
  Initials:   { type: 'Initials',   label: 'Initials',      icon: 'font_download',       tooltip: 'Signer draws or types their initials' },
  DateSigned: { type: 'DateSigned', label: 'Date Signed',   icon: 'event',               tooltip: 'Auto-filled with the date the signer completes signing' },
  Date:       { type: 'Date',       label: 'Date',          icon: 'calendar_today',      tooltip: 'Signer selects a date from a calendar picker' },
  Text:       { type: 'Text',       label: 'Text',          icon: 'text_fields',         tooltip: 'Signer enters free-form text' },
  FullName:   { type: 'FullName',   label: 'Full Name',     icon: 'person',              tooltip: 'Pre-filled with the signer\'s full name (editable)' },
  Email:      { type: 'Email',      label: 'Email',         icon: 'email',               tooltip: 'Pre-filled with the signer\'s email address (editable)' },
  Title:      { type: 'Title',      label: 'Title',         icon: 'badge',               tooltip: 'Signer enters their job title' },
  Company:    { type: 'Company',    label: 'Company',       icon: 'business',            tooltip: 'Signer enters their company name' },
  Checkbox:   { type: 'Checkbox',   label: 'Checkbox',      icon: 'check_box',           tooltip: 'Signer checks or unchecks a box. Use "Must be checked" to require agreement.' },
  Attachment: { type: 'Attachment', label: 'Attachment',    icon: 'attach_file',         tooltip: 'Signer uploads a file (PDF, DOC, JPG, PNG, TIFF - max 10 MB)' },
  Dropdown:   { type: 'Dropdown',   label: 'Option Select', icon: 'arrow_drop_down_circle',   tooltip: 'Signer picks from a list of predefined options' },
  RadioGroup: { type: 'RadioGroup', label: 'Radio Group',   icon: 'radio_button_checked', tooltip: 'Signer picks one option from visible radio buttons' },
  Note:       { type: 'Note',       label: 'Label',         icon: 'label_outline',   tooltip: 'Static text placed by the sender - visible to all signers, not editable' },
};

/**
 * Last-resort sizing rule used when the server-provided rules table is
 * unavailable (API offline at app start, etc.). Matches the C# Fallback
 * rule in AnnotationFieldTypeRules.cs so degraded-mode and online-mode
 * sizes look similar. Production code paths fetch real rules at app load;
 * this should never be the steady-state source.
 */
export const FALLBACK_FIELD_RULE = Object.freeze({
  widthMultiplier: 5.0,
  heightMultiplier: 1.2,
  minWidthPt: 60,
  minHeightPt: 14,
});

/**
 * Resolves the default (Width, Height) in PDF points for a newly-dropped
 * annotation. Mirrors the server-side AnnotationDimensionResolver.Resolve
 * formula so the UI shows exactly what the server would store. The page's
 * medianLineHeightPt drives Layer 2 (document-aware) defaults; pages
 * without text fall back to a fraction of page height (Layer 1).
 *
 * @param {string} type - annotation type name (e.g. 'Signature').
 * @param {{widthPt: number, heightPt: number, medianLineHeightPt?: number|null}} page
 * @param {Object|null} rules - type→rule map from useAnnotationFieldTypesStore.types, or null.
 * @param {number} fallbackFraction - fraction of page height to use as
 *   synthetic line height when medianLineHeightPt is null. From the store.
 * @returns {{widthPt: number, heightPt: number}}
 */
export function resolveAnnotationDefaults(type, page, rules, fallbackFraction = 0.018) {
  const rule = (rules && rules[type]) || FALLBACK_FIELD_RULE;
  const lineHeight = page?.medianLineHeightPt ?? ((page?.heightPt || 792) * fallbackFraction);
  const defaultW = lineHeight * rule.widthMultiplier;
  const defaultH = lineHeight * rule.heightMultiplier;
  return {
    widthPt: Math.max(defaultW, rule.minWidthPt),
    heightPt: Math.max(defaultH, rule.minHeightPt),
  };
}

/**
 * Returns the per-type minimum (Width, Height) for resize-handle clamping.
 * Same minimums the server enforces - so what a user can resize to in the
 * UI matches what the server will accept on save (no silent clamp-on-save).
 *
 * @param {string} type - annotation type name.
 * @param {Object|null} rules - type→rule map from useAnnotationFieldTypesStore.types.
 * @returns {{minWidthPt: number, minHeightPt: number}}
 */
export function resolveAnnotationMinimums(type, rules) {
  const rule = (rules && rules[type]) || FALLBACK_FIELD_RULE;
  return { minWidthPt: rule.minWidthPt, minHeightPt: rule.minHeightPt };
}

/**
 * Ordered list of field types for the toolbar.
 */
export const FIELD_TYPE_LIST = [
  FIELD_TYPES.Attachment,
  FIELD_TYPES.Checkbox,
  FIELD_TYPES.Company,
  FIELD_TYPES.Date,
  FIELD_TYPES.DateSigned,
  FIELD_TYPES.Email,
  FIELD_TYPES.FullName,
  FIELD_TYPES.Initials,
  FIELD_TYPES.Note,
  FIELD_TYPES.Dropdown,
  FIELD_TYPES.RadioGroup,
  FIELD_TYPES.Signature,
  FIELD_TYPES.Text,
  FIELD_TYPES.Title,
];

/**
 * Convert page-relative coordinates (0-1) to pixel coordinates.
 * @param {number} relX - X position as fraction of page width (0-1)
 * @param {number} relY - Y position as fraction of page height (0-1)
 * @param {number} pageWidthPx - Page width in pixels at current scale
 * @param {number} pageHeightPx - Page height in pixels at current scale
 */
export function relativeToPixels(relX, relY, pageWidthPx, pageHeightPx) {
  return {
    x: relX * pageWidthPx,
    y: relY * pageHeightPx,
  };
}

/**
 * Convert pixel coordinates to page-relative (0-1).
 * @param {number} px - X position in pixels
 * @param {number} py - Y position in pixels
 * @param {number} pageWidthPx - Page width in pixels at current scale
 * @param {number} pageHeightPx - Page height in pixels at current scale
 */
export function pixelsToRelative(px, py, pageWidthPx, pageHeightPx) {
  return {
    x: pageWidthPx > 0 ? px / pageWidthPx : 0,
    y: pageHeightPx > 0 ? py / pageHeightPx : 0,
  };
}

/**
 * Convert PDF points to pixels at a given scale.
 * PDF points are 1/72 inch. At scale 1.0, 1pt = 1px (matching 72dpi images).
 * At scale 2.0, 1pt = 2px (matching 144dpi images).
 */
export function ptToPixels(pt, scale) {
  return pt * scale;
}

/**
 * Convert pixels back to PDF points at a given scale.
 */
export function pixelsToPt(px, scale) {
  return scale > 0 ? px / scale : 0;
}

/**
 * Clamp a value between min and max.
 */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

// ── Canonical Text Rendering Constants ──────────────────────────────────────
// These constants define how text-based fields render across editor, ceremony,
// and burned PDF (DocumentProcessingService.cs). Changing these changes all three.
// The C# counterparts are in DocumentProcessingService.cs - keep them in sync.

/** Font family for screen rendering (Arial maps to Helvetica in PDF). */
export const FIELD_FONT_FAMILY = 'Arial, Helvetica, sans-serif';

/** Font size = box height * this ratio. */
export const FIELD_FONT_SIZE_RATIO = 0.7;

/** Minimum auto-scaled font size in points. */
export const FIELD_MIN_FONT_SIZE = 8;

/** Maximum auto-scaled font size in points. */
export const FIELD_MAX_FONT_SIZE = 24;

/** Left padding in points. */
export const FIELD_LEFT_PADDING_PT = 2;

/** Distance from bottom edge to text baseline, as fraction of box height. */
export const FIELD_BASELINE_OFFSET_RATIO = 0.15;

/**
 * Compute the canonical font size for a text field given its height in points.
 * @param {number} heightPt - Field height in PDF points
 * @returns {number} Font size in points
 */
export function computeFieldFontSize(heightPt) {
  return clamp(heightPt * FIELD_FONT_SIZE_RATIO, FIELD_MIN_FONT_SIZE, FIELD_MAX_FONT_SIZE);
}

/** Text-based field types that use canonical text rendering. */
export const TEXT_FIELD_TYPES = new Set([
  'Text', 'FullName', 'Email', 'Title', 'Company', 'DateSigned', 'Date', 'Dropdown', 'RadioGroup', 'Note'
]);
